import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const PublicComplaintLog = () => {
  const { token } = useParams();
  const [logInfo, setLogInfo] = useState(null);
  const [pestTypes, setPestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    pest_type_id: '',
    pest_type_custom: '',
    location_description: '',
    quantity: '1',
    reported_by: '',
    severity: 1,
  });

  useEffect(() => { fetchLogInfo(); }, [token]);

  const fetchLogInfo = async () => {
    try {
      const res = await fetch(`https://api.safecorepest.com/api/complaint-logs/public/${token}`);
      const data = await res.json();
      if (data.error) { setError(data.error); }
      else { setLogInfo(data.log); setPestTypes(data.pest_types || []); }
    } catch(e) { setError('Could not load form. Please try again.'); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.location_description.trim()) { alert('Please enter a location'); return; }
    if (!form.pest_type_id && !form.pest_type_custom.trim()) { alert('Please select a pest type'); return; }
    if (!form.reported_by.trim()) { alert('Please enter your name'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`https://api.safecorepest.com/api/complaint-logs/public/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          pest_type_id: form.pest_type_id || null,
          pest_type_custom: form.pest_type_custom || null,
        })
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else alert(data.error || 'Error submitting. Please try again.');
    } catch(e) { alert('Network error. Please try again.'); }
    setSubmitting(false);
  };

  const severityInfo = {
    1: { label: 'Low — sighting only, no immediate risk', color: '#16A34A', bg: '#F0FDF4' },
    2: { label: 'Medium — active evidence, monitor closely', color: '#D97706', bg: '#FFFBEB' },
    3: { label: 'High — immediate risk, requires action', color: '#DC2626', bg: '#FEF2F2' },
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F9FAFB' }}>
      <div style={{ textAlign:'center', color:'#6B7280' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
        <div>Loading form...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F9FAFB', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:320 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>⚠️</div>
        <div style={{ fontSize:18, fontWeight:700, color:'#1A3A2A', marginBottom:8 }}>Form Unavailable</div>
        <div style={{ fontSize:14, color:'#6B7280' }}>{error}</div>
        <div style={{ fontSize:12, color:'#9CA3AF', marginTop:12 }}>This QR code may have expired. Please contact your pest control provider.</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F0FDF4', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:320 }}>
        <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
        <div style={{ fontSize:22, fontWeight:700, color:'#2D5C3F', marginBottom:8 }}>Sighting Reported!</div>
        <div style={{ fontSize:14, color:'#6B7280', marginBottom:24 }}>
          Your pest sighting has been recorded. The pest control technician will review it on their next visit.
        </div>
        <div style={{ background:'white', borderRadius:12, padding:16, border:'1px solid #C6F6D5', marginBottom:16 }}>
          <div style={{ fontSize:12, color:'#6B7280', marginBottom:4 }}>Reported to</div>
          <div style={{ fontSize:14, fontWeight:600, color:'#1A3A2A' }}>{logInfo?.facility_name}</div>
          <div style={{ fontSize:13, color:'#6B7280' }}>{logInfo?.name}</div>
        </div>
        <button onClick={() => { setSubmitted(false); setForm({ pest_type_id:'', pest_type_custom:'', location_description:'', quantity:'1', reported_by:'', severity:1 }); }}
          style={{ padding:'12px 24px', background:'#2D5C3F', color:'white', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
          Report Another Sighting
        </button>
      </div>
    </div>
  );

  const categories = ['rodent','insect','bird','other'];
  const catLabels = { rodent:'🐀 Rodent', insect:'🪰 Insect', bird:'🐦 Bird', other:'🔍 Other' };

  return (
    <div style={{ minHeight:'100vh', background:'#F9FAFB' }}>
      {/* Header */}
      <div style={{ background:'#2D5C3F', padding:'16px 20px' }}>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginBottom:2 }}>PEST SIGHTING REPORT</div>
        <div style={{ fontSize:17, fontWeight:700, color:'white' }}>{logInfo?.facility_name}</div>
        <div style={{ fontSize:13, color:'rgba(255,255,255,0.7)' }}>
          {logInfo?.name}{logInfo?.section_name ? ` — ${logInfo.section_name}` : ''}
        </div>
      </div>

      <div style={{ padding:'16px', maxWidth:480, margin:'0 auto' }}>
        <div style={{ background:'#E8F5E9', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#2D5C3F' }}>
          Fill out this form to report a pest sighting. The pest control technician will be notified.
        </div>

        {/* Pest Type */}
        <div style={{ background:'white', borderRadius:10, padding:14, marginBottom:12, border:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1A3A2A', marginBottom:10 }}>What did you see? *</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:10 }}>
            {categories.map(cat => {
              const typesInCat = pestTypes.filter(p => p.category === cat);
              if (!typesInCat.length) return null;
              return (
                <div key={cat}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', marginBottom:5, textTransform:'uppercase' }}>
                    {catLabels[cat]}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                    {typesInCat.map(p => (
                      <button key={p.id} onClick={() => setForm(f=>({...f, pest_type_id: String(p.id), pest_type_custom:''}))}
                        style={{ padding:'10px 8px', borderRadius:8,
                          border:`2px solid ${form.pest_type_id===String(p.id)?'#2D5C3F':'#E5E7EB'}`,
                          background: form.pest_type_id===String(p.id) ? '#E8F5E9' : 'white',
                          color: form.pest_type_id===String(p.id) ? '#2D5C3F' : '#374151',
                          fontSize:12, fontWeight: form.pest_type_id===String(p.id) ? 700 : 400,
                          cursor:'pointer', textAlign:'center' }}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', marginBottom:5, textTransform:'uppercase' }}>
                🔍 Other
              </div>
              <button onClick={() => setForm(f=>({...f, pest_type_id:'other'}))}
                style={{ width:'100%', padding:'10px 8px', borderRadius:8,
                  border:`2px solid ${form.pest_type_id==='other'?'#2D5C3F':'#E5E7EB'}`,
                  background: form.pest_type_id==='other' ? '#E8F5E9' : 'white',
                  color: form.pest_type_id==='other' ? '#2D5C3F' : '#374151',
                  fontSize:12, cursor:'pointer' }}>
                Other / Not listed
              </button>
            </div>
          </div>
          {form.pest_type_id === 'other' && (
            <input value={form.pest_type_custom} onChange={e => setForm(f=>({...f, pest_type_custom:e.target.value}))}
              placeholder="Describe what you saw..."
              style={{ width:'100%', padding:'10px', borderRadius:8, border:'1px solid #D1D5DB', fontSize:13, boxSizing:'border-box' }} />
          )}
        </div>

        {/* Location */}
        <div style={{ background:'white', borderRadius:10, padding:14, marginBottom:12, border:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1A3A2A', marginBottom:8 }}>Where did you see it? *</div>
          <input value={form.location_description} onChange={e => setForm(f=>({...f, location_description:e.target.value}))}
            placeholder="e.g. Near dock door 3, SE corner of warehouse, kitchen floor drain..."
            style={{ width:'100%', padding:'10px', borderRadius:8, border:'1px solid #D1D5DB', fontSize:13, boxSizing:'border-box' }} />
        </div>

        {/* Quantity + Severity */}
        <div style={{ background:'white', borderRadius:10, padding:14, marginBottom:12, border:'1px solid #E5E7EB' }}>
          <div style={{ display:'flex', gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1A3A2A', marginBottom:8 }}>How many?</div>
              <input type="number" min="1" value={form.quantity} onChange={e => setForm(f=>({...f, quantity:e.target.value}))}
                style={{ width:'100%', padding:'10px', borderRadius:8, border:'1px solid #D1D5DB', fontSize:13, boxSizing:'border-box' }} />
            </div>
            <div style={{ flex:2 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1A3A2A', marginBottom:8 }}>Severity</div>
              <div style={{ display:'flex', gap:6 }}>
                {[1,2,3].map(s => (
                  <button key={s} onClick={() => setForm(f=>({...f, severity:s}))}
                    style={{ flex:1, padding:'10px 4px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                      border:`2px solid ${form.severity===s ? severityInfo[s].color : '#E5E7EB'}`,
                      background: form.severity===s ? severityInfo[s].bg : 'white',
                      color: form.severity===s ? severityInfo[s].color : '#6B7280' }}>
                    {s}
                  </button>
                ))}
              </div>
              <div style={{ fontSize:10, color: severityInfo[form.severity].color, marginTop:4 }}>
                {severityInfo[form.severity].label}
              </div>
            </div>
          </div>
        </div>

        {/* Reporter */}
        <div style={{ background:'white', borderRadius:10, padding:14, marginBottom:20, border:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1A3A2A', marginBottom:8 }}>Your name *</div>
          <input value={form.reported_by} onChange={e => setForm(f=>({...f, reported_by:e.target.value}))}
            placeholder="First and last name"
            style={{ width:'100%', padding:'10px', borderRadius:8, border:'1px solid #D1D5DB', fontSize:13, boxSizing:'border-box' }} />
        </div>

        {/* Submit */}
        <button onClick={handleSubmit} disabled={submitting}
          style={{ width:'100%', padding:'16px', background: submitting ? '#9CA3AF' : '#2D5C3F',
            color:'white', border:'none', borderRadius:10, fontSize:15, fontWeight:700,
            cursor: submitting ? 'not-allowed' : 'pointer' }}>
          {submitting ? 'Submitting...' : '📋 Submit Sighting Report'}
        </button>

        <div style={{ textAlign:'center', marginTop:16, fontSize:11, color:'#9CA3AF' }}>
          Powered by SafeCore Pest Management Platform
        </div>
      </div>
    </div>
  );
};

export default PublicComplaintLog;
